import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Checkbox, TextField } from '@mui/material';
import {
  Box,
  Button,
  DialogModal,
  Menu,
  MenuItem,
  ModalBox,
  PopupState,
  Table,
  Tooltip,
  bindMenu,
  bindTrigger,
} from 'components';
import { useIsMobile, useNotifications } from 'hooks';
import {
  ArrowRight2,
  Card,
  Code,
  DocumentDownload,
  Edit2,
  Send2,
  SmsNotification,
  Trash,
} from 'iconsax-react';

import { PaymentCreateEditForm } from './PaymentCreateEditForm';
import { PaymentEmailStatusTable } from './PaymentEmailStatusTable';
import { PaymentExportModal, PaymentExportOptions } from './PaymentExportModal';
import { paymentColumns } from './paymentColumns';

import {
  JsonView,
  copyToClipboard,
  dayjs,
  useParams,
  useSelector,
  useTranslation,
} from 'third-party';

import {
  DEFAULT_TIMEZONE,
  PersisTableFilters,
  PersisTableSort,
  PersisTables,
} from 'constants/common';

import { usePayment } from 'modules/payment';

import { appSelectors, paymentSelectors, settingSelectors } from 'selectors';

import { FilterValue } from 'components/Table/types';

import {
  downloadExportFile,
  extractFiltersFromSavedColumns,
  hasRights,
  mergeSavedColumnsWithExisting,
  userCanDownloadCheck,
} from 'utils/app';
import { canResend, isECheckCanBeEdit } from 'utils/check';
import {
  isValidCheckNumber,
  mapPaymentToRequest,
  sanitizeCheckNumber,
} from 'utils/payment';
import {
  getFromStorage,
  removeFromStorage,
  saveInStorage,
} from 'utils/storage';

import {
  APIPayment,
  APIPaymentStatus,
  APISettingName,
  APISortOrder,
  APIUserRight,
  PrintType,
} from 'types/api';

import { palette } from 'styles/theme';

const DEFAULT_PAYMENT_SORT = {
  sortField: 'createdOn',
  sortOrder: APISortOrder.DESC,
};

export const PaymentList = () => {
  const timezoneValue = useSelector(
    settingSelectors.getSettingValue(APISettingName.Timezone, DEFAULT_TIMEZONE),
  );

  const [columns, setColumns] = useState(paymentColumns(timezoneValue));
  const [selectedItem, setSelectedItem] = useState<APIPayment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [approveInput, setApproveInput] = useState('');
  const [jsonPreviewItem, setJsonPreviewItem] = useState<APIPayment | null>();
  const [emailStatusPayment, setEmailStatusPayment] = useState(false);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<string[]>([]);
  const [isBulkResendDialogOpen, setIsBulkResendDialogOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const { organizationId } = useParams();
  const { t } = useTranslation();

  const {
    loadPayments,
    loadMorePayments,
    updatePaymentFilters,
    deletePayment,
    clearPaymentFilters,
    clearPaymentList,
    downloadPayment,
    resendPayment,
    paymentEmailStatus,
    clearPaymentEmailStatus,
    approvePayment,
    exportPayments,
    bulkResendPayments,
  } = usePayment();

  const [loadedUserSettings, setLoadedUserSettings] = useState(false);
  const [isFilteringOn, setIsFilteringOn] = useState(false);
  const items = useSelector(paymentSelectors.paymentList);
  const isMobile = useIsMobile();
  const organizations = useSelector(appSelectors.organizations);
  const paymentsListLoaded = useSelector(paymentSelectors.paymentsListLoaded);
  const allPaymentsLoaded = useSelector(paymentSelectors.allPaymentsLoaded);
  const paymentFilters = useSelector(paymentSelectors.paymentFilters);
  const loadPaymentsPending = useSelector(paymentSelectors.loadPaymentsPending);
  const loadMorePaymentsPending = useSelector(
    paymentSelectors.loadMorePaymentsPending,
  );

  const exportPaymentsPending = useSelector(
    paymentSelectors.exportPaymentsPending,
  );
  const paymentEmailStatusPending = useSelector(
    paymentSelectors.paymentEmailStatusPending,
  );

  const paymentEmailStatuses = useSelector(
    paymentSelectors.paymentEmailStatuses,
  );

  const { success } = useNotifications();

  useEffect(() => {
    return () => {
      clearPaymentFilters();
      clearPaymentList();
    };
  }, [clearPaymentFilters, clearPaymentList, organizationId]);

  useEffect(() => {
    if (loadedUserSettings) {
      loadPayments();
    }
  }, [paymentFilters, loadedUserSettings, loadPayments]);

  useEffect(() => {
    const savedColumns = getFromStorage(
      PersisTables.Payments,
      paymentColumns(timezoneValue),
    );
    const savedSort = getFromStorage(
      PersisTableSort.Payments,
      DEFAULT_PAYMENT_SORT,
    );

    const columnFilters = savedColumns
      ? extractFiltersFromSavedColumns(savedColumns)
      : {};

    updatePaymentFilters({ ...columnFilters, searchSortOrders: [savedSort] });

    if (savedColumns) {
      setColumns(mergeSavedColumnsWithExisting(savedColumns, columns));
    }
    setIsFilteringOn(getFromStorage(PersisTableFilters.Payments, false));
    setLoadedUserSettings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const onColumnFilterChange = useCallback(
    (columnId: string, value: string) => {
      setColumns(
        columns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filter: value };
          }
          return column;
        }),
      );

      updatePaymentFilters({ ...paymentFilters, [columnId]: value });
    },
    [paymentFilters, columns, updatePaymentFilters],
  );

  const onAdvancedFilterChange = useCallback(
    (columnId: string, value: FilterValue) => {
      setColumns(
        columns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filterValue: value };
          }
          return column;
        }),
      );

      updatePaymentFilters({ ...paymentFilters, [columnId]: value });
    },
    [paymentFilters, columns, updatePaymentFilters],
  );

  const onFilterReset = useCallback(() => {
    updatePaymentFilters({});
    setColumns(it =>
      it.map(column => ({
        ...column,
        filter: undefined,
        filterValue: undefined,
      })),
    );
  }, [updatePaymentFilters]);

  const onColumnVisibilityChanged = useCallback(
    (columnId: string, value: boolean) => {
      setColumns(it =>
        it.map(column => {
          if (column.dataId === columnId) {
            return { ...column, hidden: !value };
          }
          return column;
        }),
      );
    },
    [],
  );

  const hasAdminAccess = hasRights(
    organizations,
    APIUserRight.PaymentProcessing,
    organizationId,
  );

  const resendableItems = useMemo(
    () => (items || []).filter(canResend),
    [items],
  );

  const toggleSelection = useCallback((id: string) => {
    setSelectedPaymentIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    const resendableIds = resendableItems.map(it => it.id);
    setSelectedPaymentIds(prev =>
      prev.length === resendableIds.length ? [] : resendableIds,
    );
  }, [resendableItems]);

  const checkboxColumn = useMemo(
    () => ({
      dataId: '__select__' as keyof APIPayment,
      label: '',
      width: 48,
      minWidth: 48,
      hidden: false,
      sortable: false,
      notFilterable: true,
      cannotBeHidden: true,
      formatter: (_: unknown, item: APIPayment) => ({
        value: '',
        element: (
          <Checkbox
            checked={selectedPaymentIds.includes(item.id)}
            disabled={!canResend(item)}
            size="small"
            onChange={() => toggleSelection(item.id)}
            onClick={e => e.stopPropagation()}
          />
        ),
      }),
    }),
    [selectedPaymentIds, toggleSelection],
  );

  const tableColumns = useMemo(
    () => (hasAdminAccess ? [checkboxColumn, ...columns] : columns),
    [hasAdminAccess, checkboxColumn, columns],
  );

  const CHECKBOX_COLUMN_ID = '__select__';

  const onBulkResendConfirm = useCallback(async () => {
    setIsBulkResendDialogOpen(false);
    await bulkResendPayments(selectedPaymentIds);
    success(
      t('paymentList.actions.bulkResend', { count: selectedPaymentIds.length }),
    );
    setSelectedPaymentIds([]);
  }, [bulkResendPayments, selectedPaymentIds, success, t]);

  const rowOptionsMenu = useCallback(
    (item: APIPayment, onRowMenuClose: () => void): React.ReactElement[] => {
      if (hasAdminAccess) {
        return [
          <MenuItem
            key={'edit'}
            disabled={!isECheckCanBeEdit(item)}
            onClick={() => {
              setSelectedItem(item);
              setIsModalOpen(true);
              onRowMenuClose();
            }}
          >
            <Edit2
              variant="TwoTone"
              color={palette.primary900}
              size="24"
              style={{ paddingRight: '0.5rem' }}
            />
            {t('table.edit')}
          </MenuItem>,
          <MenuItem
            key={'resend'}
            disabled={!canResend(item)}
            onClick={() => {
              resendPayment(item.id);
              success(t('paymentList.actions.resend'));
              onRowMenuClose();
            }}
          >
            <SmsNotification
              variant="TwoTone"
              color={palette.primary900}
              size="24"
              style={{ paddingRight: '0.5rem' }}
            />
            {t('table.resend')}
          </MenuItem>,
          <MenuItem
            key={'resend'}
            disabled={item.status !== APIPaymentStatus.Requested}
            onClick={() => {
              setSelectedItem(item);
              setIsApproveModalOpen(true);
              onRowMenuClose();
            }}
          >
            <Send2
              variant="TwoTone"
              color={palette.primary900}
              size="24"
              style={{ paddingRight: '0.5rem' }}
            />
            {t('table.approve')}
          </MenuItem>,
          <MenuItem
            key={'delete'}
            disabled={item.status === APIPaymentStatus.Void}
            onClick={() => {
              setSelectedItem(item);
              onRowMenuClose();
            }}
          >
            <Trash
              variant="TwoTone"
              color={palette.primary900}
              size="24"
              style={{ paddingRight: '0.5rem' }}
            />
            {t('table.delete')}
          </MenuItem>,
          <MenuItem
            key={'status'}
            disabled={
              !(
                item.status == APIPaymentStatus.Emailed ||
                item.status == APIPaymentStatus.Printed ||
                item.status === APIPaymentStatus.Delivered ||
                item.status == APIPaymentStatus.NotDelivered ||
                item.status == APIPaymentStatus.CouldNotPrint ||
                item.status == APIPaymentStatus.Created
              )
            }
            onClick={() => {
              paymentEmailStatus(item.id);
              setEmailStatusPayment(true);
              onRowMenuClose();
            }}
          >
            <Send2
              variant="TwoTone"
              color={palette.primary900}
              size="24"
              style={{ paddingRight: '0.5rem' }}
            />
            {t('paymentList.emailStatus')}
          </MenuItem>,
          <MenuItem
            key={'json'}
            onClick={() => {
              setJsonPreviewItem(item);
              onRowMenuClose();
            }}
          >
            <Code
              variant="TwoTone"
              color={palette.primary900}
              size="24"
              style={{ paddingRight: '0.5rem' }}
            />
            {t('table.json')}
          </MenuItem>,
          <PopupState
            key={'download'}
            variant="popover"
            popupId={`download-submenu-${item.id}`}
          >
            {popupState => (
              <>
                <MenuItem
                  {...bindTrigger(popupState)}
                  disabled={!userCanDownloadCheck(item)}
                >
                  <DocumentDownload
                    variant="TwoTone"
                    color={palette.primary900}
                    size="24"
                    style={{ paddingRight: '0.5rem' }}
                  />
                  {t('table.download')}
                  <ArrowRight2
                    variant="TwoTone"
                    size="24"
                    style={{ marginLeft: 'auto' }}
                  />
                </MenuItem>
                <Menu
                  {...bindMenu(popupState)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <MenuItem
                    onClick={async () => {
                      const link = await downloadPayment(
                        item.id,
                        PrintType.WhitePaper,
                      );
                      window.open(link.payload, '_blank');
                      popupState.close();
                      onRowMenuClose();
                    }}
                  >
                    {t('printECheck.onWhitePaper')}
                  </MenuItem>
                  <MenuItem
                    onClick={async () => {
                      const link = await downloadPayment(
                        item.id,
                        PrintType.Check,
                      );
                      window.open(link.payload, '_blank');
                      popupState.close();
                      onRowMenuClose();
                    }}
                  >
                    {t('printECheck.onCheckPaper')}
                  </MenuItem>
                </Menu>
              </>
            )}
          </PopupState>,
        ];
      }

      return [
        <PopupState
          key={'download'}
          variant="popover"
          popupId={`download-submenu-${item.id}`}
        >
          {popupState => (
            <>
              <MenuItem
                disabled={!userCanDownloadCheck(item)}
                {...bindTrigger(popupState)}
              >
                <DocumentDownload
                  variant="TwoTone"
                  color={palette.primary900}
                  size="24"
                  style={{ paddingRight: '0.5rem' }}
                />
                {t('table.download')}
                <ArrowRight2
                  variant="TwoTone"
                  size="24"
                  style={{ marginLeft: 'auto' }}
                />
              </MenuItem>
              <Menu
                {...bindMenu(popupState)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem
                  onClick={async () => {
                    const link = await downloadPayment(
                      item.id,
                      PrintType.WhitePaper,
                    );
                    window.open(link.payload, '_blank');
                    popupState.close();
                    onRowMenuClose();
                  }}
                >
                  {t('emptyECheckBlank.whitePaper')}
                </MenuItem>
                <MenuItem
                  onClick={async () => {
                    const link = await downloadPayment(
                      item.id,
                      PrintType.Check,
                    );
                    window.open(link.payload, '_blank');
                    popupState.close();
                    onRowMenuClose();
                  }}
                >
                  {t('emptyECheckBlank.checkPaper')}
                </MenuItem>
              </Menu>
            </>
          )}
        </PopupState>,
      ];
    },
    [
      downloadPayment,
      hasAdminAccess,
      paymentEmailStatus,
      resendPayment,
      success,
      t,
    ],
  );

  const onModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedItem(null);
  }, []);

  const onColumnHeaderClicked = useCallback(
    (columnDataId: string) => {
      const existingSortOrder = paymentFilters.searchSortOrders?.find(
        order => order.sortField === columnDataId,
      );

      const newSortOrder =
        existingSortOrder && existingSortOrder.sortOrder === APISortOrder.DESC
          ? APISortOrder.ASC
          : APISortOrder.DESC;

      const newSort = { sortField: columnDataId, sortOrder: newSortOrder };

      saveInStorage(PersisTableSort.Payments, newSort);

      updatePaymentFilters({
        ...paymentFilters,
        searchSortOrders: [newSort],
      });
    },
    [paymentFilters, updatePaymentFilters],
  );

  const onExportClick = () => {
    setIsExportModalOpen(true);
  };

  const onExportConfirm = async (options: PaymentExportOptions) => {
    const response = await exportPayments(options);
    const ext = options.format === 'Csv' ? 'csv' : 'xlsx';
    downloadExportFile(
      response.payload,
      `payments-export-${dayjs().format('YYYY-MM-DD')}.${ext}`,
    );
    setIsExportModalOpen(false);
  };

  const onApproveModalClose = () => {
    setIsApproveModalOpen(false);
    setSelectedItem(null);
    setApproveInput('');
  };

  const onApproveModalConfirm = () => {
    if (selectedItem) {
      approvePayment(selectedItem.id, approveInput.trim());
    }
    setIsApproveModalOpen(false);
    setSelectedItem(null);
    setApproveInput('');
  };

  return (
    <>
      <Table<APIPayment>
        draggable
        header={t('paymentList.header')}
        subHeader={t('paymentList.subHeader')}
        headerIcon={
          <Card variant="TwoTone" color={palette.primary900} size="28" />
        }
        onSaveTableSettings={settings => {
          saveInStorage(
            PersisTables.Payments,
            settings.filter(c => c.dataId !== CHECKBOX_COLUMN_ID),
          );
        }}
        onResetTableSettings={() => {
          setColumns(paymentColumns(timezoneValue));
          updatePaymentFilters({ searchSortOrders: [DEFAULT_PAYMENT_SORT] });
          removeFromStorage(PersisTables.Payments);
          removeFromStorage(PersisTableSort.Payments);
        }}
        onColumnSizeChanged={(it, width) => {
          setColumns(prevColumns =>
            prevColumns.map(column =>
              column.dataId === it ? { ...column, width: width } : column,
            ),
          );
        }}
        onColumnOrderChanged={newColumns => {
          setColumns(newColumns.filter(c => c.dataId !== CHECKBOX_COLUMN_ID));
        }}
        filterable
        filteringOn={isFilteringOn}
        onFilteringToggle={value => {
          setIsFilteringOn(value);
          saveInStorage(PersisTableFilters.Payments, value);
        }}
        allItemsLoaded={allPaymentsLoaded}
        infiniteScroll
        tableMinHeight="20rem"
        columns={tableColumns}
        items={items || []}
        sortField={
          paymentFilters.searchSortOrders?.[0]?.sortField as keyof APIPayment
        }
        sortOrder={paymentFilters.searchSortOrders?.[0]?.sortOrder}
        rowOptionsMenu={rowOptionsMenu}
        onColumnHeaderClicked={onColumnHeaderClicked}
        onColumnFilterChange={onColumnFilterChange}
        onAdvancedFilterChange={onAdvancedFilterChange}
        onColumnVisibilityChanged={onColumnVisibilityChanged}
        isExportPending={exportPaymentsPending}
        onExportClick={onExportClick}
        loadItemsPending={loadPaymentsPending}
        loadMoreItemsPending={loadMorePaymentsPending}
        onLoadMoreClicked={loadMorePayments}
        onFilterReset={onFilterReset}
        bodyRowHeight="1rem"
        headerRowHeight="0.2rem"
        itemsLoaded={paymentsListLoaded}
        onNewItemButtonClicked={
          hasAdminAccess
            ? () => {
                // Clear any previously selected row so the modal opens in
                // create mode (empty form) rather than editing a stale payment.
                setSelectedItem(null);
                setIsModalOpen(true);
              }
            : undefined
        }
        emptyText={t('table.empty')}
        loadMoreButtonText={t('table.loadMore')}
        headerChildren={
          hasAdminAccess && resendableItems.length > 0 ? (
            <Box display="inline-flex" alignItems="center" gap="0.25rem">
              <Tooltip title={t('table.resend')} arrow>
                <span>
                  <Checkbox
                    size="small"
                    checked={
                      resendableItems.length > 0 &&
                      selectedPaymentIds.length === resendableItems.length
                    }
                    indeterminate={
                      selectedPaymentIds.length > 0 &&
                      selectedPaymentIds.length < resendableItems.length
                    }
                    onChange={toggleSelectAll}
                  />
                </span>
              </Tooltip>
              {selectedPaymentIds.length > 0 && (
                <Tooltip
                  title={t('paymentList.bulkResend', {
                    count: selectedPaymentIds.length,
                  })}
                  arrow
                >
                  <Button
                    variant="text"
                    onClick={() => setIsBulkResendDialogOpen(true)}
                  >
                    <SmsNotification variant="TwoTone" size="24" />
                    &nbsp;{selectedPaymentIds.length}
                  </Button>
                </Tooltip>
              )}
            </Box>
          ) : undefined
        }
      />
      {isModalOpen && (
        <ModalBox
          width={isMobile ? '90%' : '33rem'}
          height="39rem"
          onClose={onModalClose}
        >
          <PaymentCreateEditForm
            onModalClose={onModalClose}
            paymentId={selectedItem?.id}
          />
        </ModalBox>
      )}
      <DialogModal
        onClose={() => setSelectedItem(null)}
        open={!!selectedItem && !isModalOpen}
        description={t('paymentList.deleteConfirm')}
        handleActionClick={() => {
          if (selectedItem) {
            deletePayment(selectedItem.id);
          }
          setSelectedItem(null);
        }}
      />
      <DialogModal
        onClose={onApproveModalClose}
        open={isApproveModalOpen}
        description={t('paymentList.approveConfirm')}
        handleActionClick={onApproveModalConfirm}
        title={t('paymentList.approveTitle')}
        actionButtonTitle={t('common.approve')}
        cancelButtonTitle={t('common.form.cancel')}
        isLoading={exportPaymentsPending}
        actionButtonDisabled={
          !isValidCheckNumber(approveInput) || approveInput.length < 3
        }
      >
        <TextField
          label={t('paymentList.column.checkNumber')}
          value={approveInput}
          onChange={e => setApproveInput(sanitizeCheckNumber(e.target.value))}
          inputProps={{ inputMode: 'numeric', maxLength: 100 }}
          fullWidth
          margin="normal"
        />
      </DialogModal>
      <DialogModal
        onClose={() => setJsonPreviewItem(null)}
        open={jsonPreviewItem != null}
        description={t('paymentList.jsonPreviewInfo')}
        handleActionClick={() => {
          copyToClipboard(JSON.stringify(mapPaymentToRequest(jsonPreviewItem)));
          success(t('common.copyToClipboard'));
          setJsonPreviewItem(null);
        }}
        title={t('paymentList.jsonPreviewTitle')}
        actionButtonTitle={t('common.copy')}
        cancelButtonTitle={t('common.close')}
        isLoading={exportPaymentsPending}
      >
        <Box
          id="test"
          maxHeight="15rem"
          width="30rem"
          minHeight="15rem"
          overflow="auto"
        >
          {jsonPreviewItem && (
            <JsonView
              displayDataTypes={false}
              value={mapPaymentToRequest(jsonPreviewItem)}
            />
          )}
        </Box>
      </DialogModal>
      <DialogModal
        onClose={() => setEmailStatusPayment(false)}
        open={emailStatusPayment}
        description={t('paymentList.emailStatusInfoTitle')}
        handleActionClick={() => {
          clearPaymentEmailStatus();
          setEmailStatusPayment(false);
        }}
        title={t('paymentList.emailStatusInfo')}
        actionButtonTitle={t('common.ok')}
        doNotShowCancelButton={true}
        isLoading={exportPaymentsPending}
      >
        <PaymentEmailStatusTable
          statuses={paymentEmailStatuses}
          isLoading={paymentEmailStatusPending}
        />
      </DialogModal>
      <DialogModal
        onClose={() => setIsBulkResendDialogOpen(false)}
        open={isBulkResendDialogOpen}
        description={t('paymentList.bulkResendConfirm', {
          count: selectedPaymentIds.length,
        })}
        handleActionClick={onBulkResendConfirm}
        title={t('paymentList.bulkResendTitle')}
        actionButtonTitle={t('table.resend')}
        cancelButtonTitle={t('common.form.cancel')}
      />
      {isExportModalOpen && (
        <PaymentExportModal
          onClose={() => setIsExportModalOpen(false)}
          onExport={onExportConfirm}
          isPending={exportPaymentsPending}
        />
      )}
    </>
  );
};
